import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { medicationsAPI, administrationRoutesAPI, medicationPresentationsAPI } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Plus, 
  Pencil, 
  Trash, 
  Pill,
  MagnifyingGlass,
  Spinner,
  WarningCircle,
  Power,
  CheckCircle,
  XCircle,
  Syringe,
  Package
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import EffectsEditor from '../../components/effects/EffectsEditor';

const CATEGORIES = [
  'Cardiovascular',
  'Analgésicos',
  'Sedación',
  'Emergencias',
  'Antibióticos',
  'Respiratorio',
  'Gastrointestinal',
  'Otro'
];

const UNITS = ['mg', 'mcg', 'g', 'ml', 'UI', 'mEq'];

const defaultMedication = {
  nombre: '',
  categoria: 'Otro',
  descripcion: '',
  dosis_sugerida: '',
  dosis_minima: 0,
  dosis_maxima: 0,
  unidad_dosis: 'mg',
  unidades: ['mg'],
  vias_permitidas: [],
  vias_ids: [],
  presentacion_id: '',
  presentacion_nombre: '',
  indicaciones: [],
  contraindicaciones: [],
  efectos_vitales: {}
};

export default function MedicationsPage() {
  const { isSuperAdmin, hasPermission } = useAuth();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingMed, setEditingMed] = useState(null);
  const [formData, setFormData] = useState(defaultMedication);
  const [saving, setSaving] = useState(false);

  // Dynamic data from maintainers
  const [administrationRoutes, setAdministrationRoutes] = useState([]);
  const [medicationPresentations, setMedicationPresentations] = useState([]);
  const [loadingMaintainers, setLoadingMaintainers] = useState(true);

  const canCreate = isSuperAdmin || hasPermission('medicamentos', 'crear');
  const canEdit = isSuperAdmin || hasPermission('medicamentos', 'editar');
  const canToggle = isSuperAdmin || hasPermission('medicamentos', 'activar_desactivar');

  // Load maintainers (routes and presentations)
  useEffect(() => {
    const loadMaintainers = async () => {
      setLoadingMaintainers(true);
      try {
        const [routesRes, presentationsRes] = await Promise.all([
          administrationRoutesAPI.getAll(),
          medicationPresentationsAPI.getAll()
        ]);
        setAdministrationRoutes(routesRes.data || []);
        setMedicationPresentations(presentationsRes.data || []);
      } catch (error) {
        console.error('Error loading maintainers:', error);
        toast.error('Error al cargar mantenedores');
      } finally {
        setLoadingMaintainers(false);
      }
    };
    loadMaintainers();
  }, []);

  useEffect(() => {
    loadMedications();
  }, [showInactive]);

  const loadMedications = async () => {
    try {
      const response = await medicationsAPI.getAll(showInactive);
      setMedications(response.data);
    } catch (error) {
      toast.error('Error al cargar medicamentos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (med = null) => {
    if (med) {
      setEditingMed(med);
      setFormData({
        ...defaultMedication,
        ...med,
        unidades: med.unidades || ['mg'],
        vias_permitidas: med.vias_permitidas || [],
        vias_ids: med.vias_ids || [],
        presentacion_id: med.presentacion_id || '',
        presentacion_nombre: med.presentacion_nombre || ''
      });
    } else {
      setEditingMed(null);
      setFormData(defaultMedication);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMed(null);
    setFormData(defaultMedication);
  };

  const handleSave = async () => {
    if (!formData.nombre || !formData.dosis_sugerida) {
      toast.error('Complete los campos obligatorios (Nombre y Dosis sugerida)');
      return;
    }

    // Validación: al menos una vía de administración
    if (!formData.vias_ids || formData.vias_ids.length === 0) {
      toast.error('Debe seleccionar al menos una vía de administración');
      return;
    }

    // Validación: presentación obligatoria
    if (!formData.presentacion_id) {
      toast.error('Debe seleccionar una presentación');
      return;
    }

    setSaving(true);
    try {
      // Preparar datos con nombres de vías basados en IDs
      const viasNombres = formData.vias_ids.map(viaId => {
        const route = administrationRoutes.find(r => r.id === viaId);
        return route ? route.codigo : viaId;
      });

      const presentation = medicationPresentations.find(p => p.id === formData.presentacion_id);

      const dataToSave = {
        ...formData,
        vias_permitidas: viasNombres,
        presentacion_nombre: presentation ? presentation.nombre : ''
      };

      if (editingMed) {
        await medicationsAPI.update(editingMed.id, dataToSave);
        toast.success('Medicamento actualizado');
      } else {
        await medicationsAPI.create(dataToSave);
        toast.success('Medicamento creado');
      }
      handleCloseModal();
      loadMedications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (med) => {
    if (!window.confirm(`¿Eliminar ${med.nombre}?`)) return;

    try {
      await medicationsAPI.delete(med.id);
      toast.success('Medicamento eliminado');
      loadMedications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleToggleActive = async (med) => {
    try {
      await medicationsAPI.toggleActive(med.id);
      toast.success(med.is_active !== false ? 'Medicamento desactivado' : 'Medicamento activado');
      loadMedications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleUnitChange = (value) => {
    const units = value.split(',').map(u => u.trim()).filter(u => u);
    setFormData({ ...formData, unidades: units.length ? units : ['mg'] });
  };

  // Toggle route selection by ID
  const handleRouteToggle = (routeId) => {
    const currentIds = formData.vias_ids || [];
    const newIds = currentIds.includes(routeId)
      ? currentIds.filter(id => id !== routeId)
      : [...currentIds, routeId];
    setFormData({ ...formData, vias_ids: newIds });
  };

  const filteredMedications = medications.filter(med => {
    const matchesSearch = (med.nombre || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
                         (med.categoria || '').toLowerCase().includes((searchTerm || '').toLowerCase());
    const matchesCategory = selectedCategory === 'all' || med.categoria === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Helper to get route name by ID
  const getRouteName = (routeId) => {
    const route = administrationRoutes.find(r => r.id === routeId);
    return route ? route.codigo : routeId;
  };

  // Helper to get presentation name by ID
  const getPresentationName = (presentationId) => {
    const presentation = medicationPresentations.find(p => p.id === presentationId);
    return presentation ? presentation.nombre : presentationId;
  };

  if (loading || loadingMaintainers) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900" data-testid="medications-title">
              Catálogo de Medicamentos
            </h1>
            <p className="text-slate-500">
              {medications.length} medicamentos registrados
            </p>
          </div>
          <div className="flex items-center gap-3">
            {canToggle && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Mostrar inactivos
              </label>
            )}
            <Button variant="outline" className="gap-2" data-testid="bulk-import-link-medications"
              onClick={() => window.location.assign('/app/admin/bulk-import?module=medications')}>
              Carga Masiva
            </Button>
            {canCreate && (
              <Button onClick={() => handleOpenModal()} className="gap-2" data-testid="add-medication-btn">
                <Plus size={18} />
                Agregar Medicamento
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar medicamentos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Medications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMedications.map(med => (
            <Card key={med.id} className={`hover:shadow-md transition-shadow ${med.is_active === false ? 'opacity-60' : ''}`} data-testid={`medication-card-${med.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${med.is_active === false ? 'bg-slate-100' : 'bg-blue-100'}`}>
                      <Pill size={20} className={med.is_active === false ? 'text-slate-400' : 'text-blue-600'} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{med.nombre}</CardTitle>
                      <div className="flex items-center gap-1 mt-1">
                        <Badge variant="secondary">{med.categoria}</Badge>
                        {med.is_active === false && (
                          <Badge className="bg-slate-100 text-slate-600">Inactivo</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  {!med.is_default && (
                    <div className="flex gap-1">
                      {canEdit && (
                        <Button variant="ghost" size="sm" onClick={() => handleOpenModal(med)} data-testid={`edit-${med.id}`}>
                          <Pencil size={16} />
                        </Button>
                      )}
                      {canToggle && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className={med.is_active !== false ? 'text-orange-500 hover:text-orange-700' : 'text-green-500 hover:text-green-700'}
                          onClick={() => handleToggleActive(med)}
                          title={med.is_active !== false ? 'Desactivar' : 'Activar'}
                        >
                          <Power size={16} />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="text-slate-500">Dosis sugerida:</span>{' '}
                  <span className="font-medium">{med.dosis_sugerida}</span>
                </div>
                {(med.dosis_minima > 0 || med.dosis_maxima > 0) && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Rango:</span>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {med.dosis_minima || 0} - {med.dosis_maxima || '∞'} {med.unidad_dosis || 'mg'}
                    </Badge>
                  </div>
                )}
                {/* Presentación */}
                {(med.presentacion_nombre || med.presentacion_id) && (
                  <div className="flex items-center gap-2">
                    <Package size={14} className="text-slate-400" />
                    <span className="text-slate-500">Presentación:</span>
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      {med.presentacion_nombre || getPresentationName(med.presentacion_id)}
                    </Badge>
                  </div>
                )}
                {/* Vías de administración */}
                <div className="flex flex-wrap gap-1">
                  <Syringe size={14} className="text-slate-400 mr-1" />
                  {med.vias_permitidas?.map(via => (
                    <Badge key={via} variant="outline" className="text-xs">{via}</Badge>
                  ))}
                </div>
                {med.is_default && (
                  <Badge variant="secondary" className="bg-slate-100 text-slate-600">Sistema</Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredMedications.length === 0 && (
          <Card className="p-8 text-center">
            <WarningCircle size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No se encontraron medicamentos</p>
          </Card>
        )}

        {/* Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{editingMed ? 'Editar Medicamento' : 'Nuevo Medicamento'}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    placeholder="Ej: Adrenalina"
                    data-testid="med-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select 
                    value={formData.categoria} 
                    onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                  >
                    <SelectTrigger data-testid="med-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dosis">Dosis sugerida (texto descriptivo) *</Label>
                  <Input
                    id="dosis"
                    value={formData.dosis_sugerida}
                    onChange={(e) => setFormData({ ...formData, dosis_sugerida: e.target.value })}
                    placeholder="Ej: 1 mg cada 3-5 min"
                    data-testid="med-dose-input"
                  />
                </div>

                {/* Dosis Mínima y Máxima */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="dosis_minima">Dosis Mínima</Label>
                    <Input
                      id="dosis_minima"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.dosis_minima || ''}
                      onChange={(e) => setFormData({ ...formData, dosis_minima: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      data-testid="med-dose-min-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dosis_maxima">Dosis Máxima</Label>
                    <Input
                      id="dosis_maxima"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.dosis_maxima || ''}
                      onChange={(e) => setFormData({ ...formData, dosis_maxima: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      data-testid="med-dose-max-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unidad</Label>
                    <Select 
                      value={formData.unidad_dosis || 'mg'} 
                      onValueChange={(value) => setFormData({ ...formData, unidad_dosis: value })}
                    >
                      <SelectTrigger data-testid="med-dose-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNITS.map(unit => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.dosis_minima > 0 && formData.dosis_maxima > 0 && formData.dosis_minima >= formData.dosis_maxima && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <WarningCircle size={14} />
                    La dosis mínima debe ser menor que la máxima
                  </p>
                )}

                <div className="space-y-2">
                  <Label htmlFor="unidades">Unidades de dosis (separadas por coma)</Label>
                  <Input
                    id="unidades"
                    value={formData.unidades.join(', ')}
                    onChange={(e) => handleUnitChange(e.target.value)}
                    placeholder="mg, mcg, ml"
                    data-testid="med-units-input"
                  />
                </div>

                {/* Vías de administración dinámicas */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Syringe size={16} className="text-blue-500" />
                    Vías de administración *
                  </Label>
                  <p className="text-xs text-slate-500 mb-2">Seleccione al menos una vía</p>
                  <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50">
                    {administrationRoutes.map(route => (
                      <Badge
                        key={route.id}
                        variant={(formData.vias_ids || []).includes(route.id) ? 'default' : 'outline'}
                        className={`cursor-pointer transition-all ${
                          (formData.vias_ids || []).includes(route.id) 
                            ? 'bg-blue-600 hover:bg-blue-700' 
                            : 'hover:bg-slate-100'
                        }`}
                        onClick={() => handleRouteToggle(route.id)}
                        data-testid={`route-${route.codigo}`}
                        title={route.descripcion}
                      >
                        {route.codigo} - {route.nombre}
                      </Badge>
                    ))}
                  </div>
                  {(!formData.vias_ids || formData.vias_ids.length === 0) && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <WarningCircle size={14} />
                      Debe seleccionar al menos una vía
                    </p>
                  )}
                </div>

                {/* Presentación dinámica */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Package size={16} className="text-purple-500" />
                    Presentación *
                  </Label>
                  <Select 
                    value={formData.presentacion_id || ''} 
                    onValueChange={(value) => setFormData({ ...formData, presentacion_id: value })}
                  >
                    <SelectTrigger data-testid="med-presentation-select">
                      <SelectValue placeholder="Seleccione una presentación" />
                    </SelectTrigger>
                    <SelectContent>
                      {medicationPresentations.map(pres => (
                        <SelectItem key={pres.id} value={pres.id}>
                          <div className="flex items-center gap-2">
                            <span>{pres.nombre}</span>
                            {pres.unidad_asociada && (
                              <span className="text-slate-400 text-xs">({pres.unidad_asociada})</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!formData.presentacion_id && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <WarningCircle size={14} />
                      Debe seleccionar una presentación
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="descripcion">Descripción</Label>
                  <Textarea
                    id="descripcion"
                    value={formData.descripcion || ''}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    placeholder="Descripción del medicamento..."
                    data-testid="med-description-input"
                  />
                </div>

                {/* Physiological Effects Editor — Fase 2 (solo al editar) */}
                {editingMed && (
                  <div className="pt-4 mt-2 border-t border-slate-200">
                    <EffectsEditor
                      sourceType="medication"
                      sourceId={editingMed.id}
                      sourceName={editingMed.nombre}
                      disabled={!canEdit}
                    />
                  </div>
                )}
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseModal}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-medication-btn">
                {saving ? <Spinner size={18} className="animate-spin" /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
